import Luiv3Constants from '@app/lui3/constants';
import {useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';

import EmailScreen from './EmailScreen';
import LoginScreen from './LoginScreen';
import {
  LOXO_EMAIL_SYNC_SMART_ALERT_ACTION,
  markCurrentMessageApprovedForSend,
  readTaskpaneInitializationContext,
  sendCurrentMessage,
} from './outlookComposeItem';
import {
  RoamingSettingsProvider,
  useRoamingSettings,
} from './RoamingSettingsContext';
import SendModal from './SendModal';
import {useSendReviewDialog} from './useSendReviewDialog';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100%;
  background-color: ${Luiv3Constants.baseColor['gray-50']};
  padding: 1rem;
`;

const Content = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  border: 1px solid ${Luiv3Constants.baseColor['gray-200']};
  background-color: ${Luiv3Constants.baseColor['gray-0']};
  border-radius: 0.5rem;
`;

const SendReviewContent = styled(Content)`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 1rem;
`;

const DialogOpeningMessage = styled.div`
  padding: 1rem;
  color: ${Luiv3Constants.baseColor['gray-600']};
  font-size: 0.875rem;
`;

const isOpenSendReviewContext = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return (
    (value as {action?: unknown}).action === LOXO_EMAIL_SYNC_SMART_ALERT_ACTION
  );
};

const AppShell = () => {
  const {podUrl, refreshToken} = useRoamingSettings();
  const [initializationContextLoaded, setInitializationContextLoaded] =
    useState(false);
  const [showSendReview, setShowSendReview] = useState(false);
  const [dialogOpenFailed, setDialogOpenFailed] = useState(false);

  const isSignedIn = Boolean(refreshToken && podUrl);

  useEffect(() => {
    void readTaskpaneInitializationContext()
      .then((context) => {
        if (isOpenSendReviewContext(context)) {
          setDialogOpenFailed(false);
          setShowSendReview(true);
        }
      })
      .catch(() => {
        // Initialization context only controls the optional send-review flow.
      })
      .finally(() => {
        setInitializationContextLoaded(true);
      });
  }, []);

  const handleSendReviewSubmit = useCallback(async () => {
    await markCurrentMessageApprovedForSend();
    await sendCurrentMessage();
  }, []);

  const handleSendReviewCancel = useCallback(() => {
    setShowSendReview(false);
  }, []);

  const handleSendReviewDialogOpenFailed = useCallback(() => {
    setDialogOpenFailed(true);
  }, []);

  useSendReviewDialog({
    enabled:
      initializationContextLoaded &&
      isSignedIn &&
      showSendReview &&
      !dialogOpenFailed,
    onClose: handleSendReviewCancel,
    onOpenFailed: handleSendReviewDialogOpenFailed,
  });

  if (!initializationContextLoaded) {
    return (
      <Wrapper>
        <Content>
          <DialogOpeningMessage>Loading Loxo...</DialogOpeningMessage>
        </Content>
      </Wrapper>
    );
  }

  if (isSignedIn && showSendReview) {
    return (
      <Wrapper>
        {dialogOpenFailed ? (
          <SendReviewContent>
            <SendModal
              onCancel={handleSendReviewCancel}
              onSubmit={handleSendReviewSubmit}
            />
          </SendReviewContent>
        ) : (
          <Content>
            <DialogOpeningMessage>
              Opening the Loxo send review...
            </DialogOpeningMessage>
          </Content>
        )}
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Content>{isSignedIn ? <EmailScreen /> : <LoginScreen />}</Content>
    </Wrapper>
  );
};

const App = () => (
  <RoamingSettingsProvider>
    <AppShell />
  </RoamingSettingsProvider>
);

export default App;
